import type { ReactNode } from 'react';

/**
 * Compact section card used by the HR create/edit forms. Gives every group of
 * fields the same header (icon, title, short description) and spacing so long
 * forms read as tidy, scannable blocks instead of one continuous page.
 */
export function FormSection({
  icon,
  title,
  description,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-tight text-text">{title}</h2>
            {description && (
              <p className="mt-0.5 text-[0.6875rem] leading-snug text-text-muted">{description}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Label + control + validation message, with consistent compact spacing. */
export function FormField({
  label,
  required,
  error,
  hint,
  className = '',
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label className="mb-1.5 flex items-center gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted">
        {label}
        {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-[0.6875rem] text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[0.6875rem] text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
